import React, { useState } from 'react';
import { addHours, format } from 'date-fns';
import { addLocale, locale } from 'primereact/api';
import { toast } from 'react-toastify';
import { ApiAdimplencia } from '../../../../../services/api.js';
import { DESCONTO_OPTIONS } from './constants.js';
import RecebivelConfirmDialog from './components/dialogs/RecebivelConfirmDialog.jsx';
import RecebivelCreateDialog from './components/dialogs/RecebivelCreateDialog.jsx';
import RecebivelListDialog from './components/dialogs/RecebivelListDialog.jsx';
import RecebivelSimulacaoDialog from './components/dialogs/RecebivelSimulacaoDialog.jsx';
import RecebivelUpdateDialog from './components/dialogs/RecebivelUpdateDialog.jsx';
import { useRecebivelSimulacao } from './hooks/useRecebivelSimulacao.js';
import { formatarAlteracoes } from './utils/recebivelFormatters.js';
import { isSimulaParcelaValida, validateRecebivelSubmitFields, validateRecebivelUpdateFields } from './utils/recebivelValidators.js';

/**
 * Container principal do fluxo de recebiveis de adimplencia.
 *
 * @param {{close: () => void, reload: () => void, itemInfo: Record<string, any>}} props
 * @returns {JSX.Element}
 */
export default function RecebivelTipoContratanteContainer({ close, reload, itemInfo }) {
    const [showConfirm, setShowConfirm] = useState(false);
    const [listaRecebivelTipos, setListaRecebivelTipos] = useState([]);

    const [showCreate, setShowCreate] = useState(false);
    const [showUpdate, setShowUpdate] = useState(false);

    const [showSimula, setShowSimula] = useState(false);

    const [recebivelTipoData, setRecebivelTipoData] = useState({});
    const [recebivelTipos, setRecebivelTipos] = useState([]);
    const [recebivelTipoId, setRecebivelTipoId] = useState('');
    const [recebivelTipoValor, setRecebivelTipoValor] = useState('');
    const [recebivelTipoDesconto, setRecebivelTipoDesconto] = useState('');
    const [recebivelTipoDataInicio, setRecebivelTipoDataInicio] = useState('');
    const [recebivelTipoDataFim, setRecebivelTipoDataFim] = useState(null);
    const [periodicidade, setPeriodicidade] = useState('Mensal');
    const [recebivelTipoVencimento, setRecebivelTipoVencimento] = useState(null);
    const [recebivelTipoDescontoTipo, setRecebivelTipoDescontoTipo] = useState('0');
    const [recebivelTipoTaxaCredor, setRecebivelTipoTaxaCredor] = useState(10);
    const [recebivelTipoContratanteDestino, setRecebivelTipoContratanteDestino] = useState(null);

    const [rowData, setRowData] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    const [recebivelTipoDescontoSimula, setRecebivelTipoDescontoSimula] = useState('');
    const [recebivelTipoDescontoTipoSimula, setRecebivelTipoDescontoTipoSimula] = useState('0');
    const [parcelaSimulado, setParcelaSimulado] = useState(0);
    const [simulaParcela, setSimulaParcela] = useState('');
    const [valorParcela, setValorParcela] = useState('');

    const [descontoAtivado, setDescontoAtivo] = useState(false);
    const [mostraDateSimulado, setMostraDateSimulado] = useState('');

    addLocale('pt-BR', {
        firstDayOfWeek: 1,
        dayNames: ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'],
        dayNamesShort: ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'],
        dayNamesMin: ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'],
        monthNames: ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'],
        monthNamesShort: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
        today: 'Hoje',
        clear: 'Claro'
    });
    locale('pt-BR');

    const { handleConfirmSimula, parseNumeroParcelasInput } = useRecebivelSimulacao({
        periodicidade,
        recebivelTipoDataInicio,
        setRecebivelTipoDataFim,
        setMostraDateSimulado,
        simulaParcela,
        recebivelTipoValor,
        recebivelTipoDescontoSimula,
        recebivelTipoDescontoTipoSimula,
        setDescontoAtivo,
        setParcelaSimulado,
        setValorParcela,
        valorParcela,
        setShowSimula,
        setRecebivelTipoValor,
    });

    /**
     * Atualiza o estado de parcelas durante digitacao, sem depender de blur.
     *
     * @param {string|number|null|undefined} rawValue
     */
    function handleSimulaParcelaInputChange(rawValue) {
        if (rawValue === '' || rawValue === null || rawValue === undefined) {
            setSimulaParcela('');
            return;
        }

        const parsedValue = parseNumeroParcelasInput(rawValue);
        setSimulaParcela(parsedValue === null ? '' : parsedValue);
    }

    //chamada de api
    async function getContratanteRecebivelTipos(contratanteId) {
        const newData = [];
        try {
            const { data } = await ApiAdimplencia.get(`contratanteRecebivelTipo/buscar/${contratanteId}`);

            data.results.map(item => { newData.push(item); });
            setListaRecebivelTipos(newData);
        } catch (error) {
            toast.error('Houve um errro ao carregar os recebiveis tipos.');
        }
    }

    async function getRecebivelTipos(credorId) {
        setIsLoading(true);
        const newData = [];
        try {
            const { data } = await ApiAdimplencia.get(`/recebivelTipo/listarTodos/${credorId}`);

            data.map(item => {
                newData.push({ label: item.recebivel_tipo_descricao, value: item.recebivel_tipo_id });
            });
            setRecebivelTipos(newData);
        } catch (error) {
            toast.error('Houve um erro ao carregar recebíveis.');
        }
        setIsLoading(false);
    }

    async function addRecebivelTipo() {
        setIsLoading(true);
        try {
            const { data } = await ApiAdimplencia.post('contratanteRecebivelTipo/inserir', recebivelTipoData);

            if (data) {
                toast.success('Recebível tipo de cliente adicionado com sucesso!');
                atualizaEFecha();
                getContratanteRecebivelTipos(itemInfo.contratante_id);
                getRecebivelTipos(itemInfo.credor_id);
            } else {
                toast.error('Houve um erro ao adicionar o recebível tipo de contratante.');
            }

        } catch (error) {
            toast.error('Houve um erro ao adicionar o recebível tipo de contratante.');
        }
        setIsLoading(false);
    }

    async function editRecebivelTipo() {
        setIsLoading(true);
        try {
            const { data } = await ApiAdimplencia.put('contratanteRecebivelTipo/atualizar', recebivelTipoData);

            if (data) {
                toast.success('Recebível tipo de contratante atualizado com sucesso!');
                atualizaEFecha();
                getContratanteRecebivelTipos(itemInfo.contratante_id);
                getRecebivelTipos(itemInfo.credor_id);
            } else {
                toast.error('Houve um erro ao atualizar o recebível tipo de contratante.');
            }

        } catch (error) {
            toast.error('Houve um erro ao atualizar o recebível tipo de contratante.');
        }
        setIsLoading(false);
    }

    function handleSubmit(event) {
        event.preventDefault();
        const submitValidationError = validateRecebivelSubmitFields({
            recebivelTipoId,
            recebivelTipoValor,
            recebivelTipoDataInicio,
            recebivelTipoVencimento,
            recebivelTipoDesconto,
            recebivelTipoDescontoTipo,
            credorId: itemInfo?.credor_id,
        });

        if (submitValidationError) {
            return toast.info(submitValidationError);
        }

        setRecebivelTipoData({
            valor: recebivelTipoValor,
            data_inicio: recebivelTipoDataInicio === '' ? '' : format(recebivelTipoDataInicio, 'yyyy-MM-dd'),
            data_fim: recebivelTipoDataFim === null ? '' : format(recebivelTipoDataFim, 'yyyy-MM-dd'),
            vencimento: recebivelTipoVencimento ? recebivelTipoVencimento : null,
            desconto: recebivelTipoDesconto === '' ? null : recebivelTipoDesconto,
            desconto_tipo: recebivelTipoDescontoTipo === '0' || recebivelTipoDescontoTipo === '' ? null : recebivelTipoDescontoTipo,
            contratante_id: itemInfo.contratante_id,
            recebivel_tipo_id: recebivelTipoId,
            con_participante_id: recebivelTipoContratanteDestino,
            taxa_credor: recebivelTipoContratanteDestino === null ? '' : recebivelTipoTaxaCredor,
            frequencia: periodicidade
        });

        setShowConfirm('add');
    }

    function handleUpdate(event) {
        event.preventDefault();

        const updateValidationError = validateRecebivelUpdateFields({
            recebivelTipoValor,
            recebivelTipoDataInicio,
            recebivelTipoVencimento,
            recebivelTipoDesconto,
            recebivelTipoDescontoTipo,
            credorId: itemInfo?.credor_id,
        });

        if (updateValidationError) {
            return toast.info(updateValidationError);
        }

        setRecebivelTipoData({
            valor: recebivelTipoValor,
            data_inicio: typeof recebivelTipoDataInicio === 'string' ? recebivelTipoDataInicio : format(recebivelTipoDataInicio, 'yyyy-MM-dd'),
            data_fim: recebivelTipoDataFim === null || recebivelTipoDataFim === '' ? '' : format(recebivelTipoDataFim, 'yyyy-MM-dd'),
            vencimento: recebivelTipoVencimento ? recebivelTipoVencimento : null,
            desconto: recebivelTipoDesconto === '' ? null : recebivelTipoDesconto,
            desconto_tipo: recebivelTipoDescontoTipo === '' || recebivelTipoDescontoTipo === '0' ? null : recebivelTipoDescontoTipo,
            contratante_id: itemInfo.contratante_id,
            recebivel_tipo_id: recebivelTipoId,
            con_participante_id: recebivelTipoContratanteDestino,
            taxa_credor: recebivelTipoContratanteDestino === null ? '' : recebivelTipoTaxaCredor,
            con_recebivel_tipo_id: rowData.con_recebivel_tipo_id,
            frequencia: periodicidade
        });

        setShowConfirm('upd');
    }

    function atualizaEFecha() {
        setRecebivelTipoId('');
        setRecebivelTipoValor('');
        setRecebivelTipoDesconto('');
        setRecebivelTipoVencimento('');
        setRecebivelTipoDataInicio('');
        setRecebivelTipoDataFim(null);
        setRecebivelTipoDescontoTipo('0');
        setRecebivelTipoContratanteDestino(null);
        setRecebivelTipoTaxaCredor('');
        setRecebivelTipoData({});
        setShowConfirm(false);
        setShowCreate(false);
        setShowUpdate(false);
        setShowSimula(false);
        setSimulaParcela('');
    }

    function handleShowSimulacao() {
        setRecebivelTipoDataInicio('');
        setRecebivelTipoDataFim(null);
        setSimulaParcela(1);
        setRecebivelTipoValor();
        setRecebivelTipoDescontoTipo('0');
        setRecebivelTipoDesconto('');
        setParcelaSimulado(0);
        setRecebivelTipoDescontoSimula('');
        setRecebivelTipoDescontoTipoSimula('0');
    }

    function handleHideSimulacao() {
        setShowSimula(false);
        setRecebivelTipoDataInicio('');
        setRecebivelTipoValor('');
        setRecebivelTipoDescontoTipo('0');
        setRecebivelTipoDesconto('');
        setSimulaParcela('');
        setRecebivelTipoDataFim(null);
        setMostraDateSimulado('');
        setParcelaSimulado(0);
        setDescontoAtivo(false);
        setRecebivelTipoDescontoSimula('');
        setRecebivelTipoDescontoTipoSimula('0');
    }

    function handleShowUpdate() {
        const {
            recebivel_tipo_id,
            valor,
            desconto,
            vencimento,
            rec_contratante_inicio,
            rec_contratante_fim,
            desconto_tipo,
            con_participante_id,
            fee_credor,
            frequencia
        } = rowData;

        setRecebivelTipoId(recebivel_tipo_id);
        setRecebivelTipoValor(valor);
        setRecebivelTipoDesconto(desconto === null ? null : desconto);
        setRecebivelTipoVencimento(vencimento === null ? '' : String(vencimento));
        setRecebivelTipoDataInicio(rec_contratante_inicio === null ? '' : addHours(new Date(rec_contratante_inicio), 3));
        setRecebivelTipoDataFim(rec_contratante_fim === null ? '' : addHours(new Date(rec_contratante_fim), 3));
        setRecebivelTipoDescontoTipo(desconto_tipo === null ? '0' : desconto_tipo);
        setRecebivelTipoContratanteDestino(con_participante_id === null ? null : con_participante_id);
        setRecebivelTipoTaxaCredor(con_participante_id === null ? '' : fee_credor);
        setPeriodicidade(frequencia === null ? '' : frequencia);
    }

    const simulaParcelaValida = isSimulaParcelaValida(simulaParcela);

    const formState = {
        recebivelTipoId,
        recebivelTipos,
        recebivelTipoValor,
        recebivelTipoDataInicio,
        recebivelTipoDataFim,
        recebivelTipoDescontoTipo,
        recebivelTipoDesconto,
        recebivelTipoVencimento,
        periodicidade,
    };

    const formActions = {
        setRecebivelTipoId,
        setRecebivelTipoValor,
        setRecebivelTipoDataInicio,
        setRecebivelTipoDataFim,
        setRecebivelTipoDescontoTipo,
        setRecebivelTipoDesconto,
        setRecebivelTipoVencimento,
        setPeriodicidade,
    };

    const simulacaoFormState = {
        recebivelTipoDataInicio,
        recebivelTipoValor,
        setRecebivelTipoDataInicio,
        setRecebivelTipoValor,
    };

    const simulacaoState = {
        recebivelTipoDescontoTipoSimula,
        recebivelTipoDescontoSimula,
        simulaParcela,
        simulaParcelaValida,
        descontoAtivado,
        parcelaSimulado,
        valorParcela,
        mostraDateSimulado,
    };

    const simulacaoActions = {
        setRecebivelTipoDescontoTipoSimula,
        setRecebivelTipoDescontoSimula,
        setSimulaParcela,
        handleSimulaParcelaInputChange,
    };

    return (
        <>
            {showConfirm && (
                <RecebivelConfirmDialog
                    visible
                    mode={showConfirm}
                    recebivelTipoData={recebivelTipoData}
                    isLoading={isLoading}
                    onAccept={() => { showConfirm === 'add' ? addRecebivelTipo() : editRecebivelTipo(); }}
                    onReject={() => { setShowConfirm(false); }}
                />
            )}

            {showCreate && (
                <RecebivelCreateDialog
                    visible
                    onClose={atualizaEFecha}
                    onOpenSimulacao={() => { setShowSimula(true); }}
                    onSubmit={handleSubmit}
                    formState={formState}
                    actions={formActions}
                    descontoOptions={DESCONTO_OPTIONS}
                />
            )}

            {showSimula && (
                <RecebivelSimulacaoDialog
                    visible
                    onShow={handleShowSimulacao}
                    onHide={handleHideSimulacao}
                    onSubmit={handleSubmit}
                    onConfirmSimulacao={() => { handleConfirmSimula(); }}
                    formState={simulacaoFormState}
                    simulacaoState={simulacaoState}
                    actions={simulacaoActions}
                    descontoOptions={DESCONTO_OPTIONS}
                />
            )}

            {showUpdate && (
                <RecebivelUpdateDialog
                    visible
                    itemInfo={itemInfo}
                    onClose={atualizaEFecha}
                    onShow={handleShowUpdate}
                    onSubmit={handleUpdate}
                    formState={formState}
                    actions={formActions}
                    descontoOptions={DESCONTO_OPTIONS}
                />
            )}

            <RecebivelListDialog
                visible
                itemInfo={itemInfo}
                isLoading={isLoading}
                listaRecebivelTipos={listaRecebivelTipos}
                onHide={close}
                onShow={() => {
                    getContratanteRecebivelTipos(itemInfo.contratante_id);
                    getRecebivelTipos(itemInfo.credor_id);
                }}
                onOpenCreate={() => { setShowCreate(true); }}
                onSelectRow={(selectedRowData) => {
                    setRowData(selectedRowData);
                    setShowUpdate(true);
                }}
                formatarAlteracoes={formatarAlteracoes}
            />
        </>
    );
}
